import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CoreService } from 'src/app/common/http-services/core.service';
import {
  OpportunityItem,
  OpportunitiesWorkspaceResponse,
  OpportunityUpdateRequest,
  OpportunityRefreshRequest,
  OpportunityRefreshResult,
  InspectUrlRequest,
  UrlInspectionResult,
} from './models/opportunity.models';

@Injectable({ providedIn: 'root' })
export class OpportunitiesApiService {
  public constructor(private core: CoreService<{}>) {}

  public async getOpportunities(projectId: string): Promise<{ data: OpportunitiesWorkspaceResponse | null; error: any }> {
    const response: { data: OpportunitiesWorkspaceResponse | null; error: any } = { data: null, error: null };
    try {
      const rawResponse = await firstValueFrom(
        this.core.get(`${environment.seoUrl}/opportunities/project/${projectId}`)
      );
      response.data = this.normalizeWorkspaceResponse(rawResponse);
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async getOpportunityById(id: string, projectId: string): Promise<{ data: OpportunityItem | null; error: any }> {
    const response: { data: OpportunityItem | null; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.get(`${environment.seoUrl}/opportunities/${id}/project/${projectId}`)
      ) as OpportunityItem;
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async updateOpportunity(
    id: string,
    projectId: string,
    request: OpportunityUpdateRequest
  ): Promise<{ data: OpportunityItem | null; error: any }> {
    const response: { data: OpportunityItem | null; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.bulkPut(`${environment.seoUrl}/opportunities/${id}/project/${projectId}`, request)
      ) as OpportunityItem;
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async deleteOpportunity(id: string, projectId: string): Promise<{ data: any; error: any }> {
    const response: { data: any; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.delete(`${environment.seoUrl}/opportunities/${id}/project/${projectId}`)
      );
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async refreshOpportunities(
    projectId: string,
    request: OpportunityRefreshRequest
  ): Promise<{ data: OpportunityRefreshResult | null; error: any }> {
    const response: { data: OpportunityRefreshResult | null; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.post(`${environment.seoUrl}/opportunities/refresh/project/${projectId}`, request)
      ) as OpportunityRefreshResult;
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async regenerateSummary(id: string, projectId: string): Promise<{ data: OpportunityItem | null; error: any }> {
    const response: { data: OpportunityItem | null; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.post(`${environment.seoUrl}/opportunities/${id}/project/${projectId}/summary/regenerate`, {})
      ) as OpportunityItem;
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  public async inspectUrl(request: InspectUrlRequest): Promise<{ data: UrlInspectionResult | null; error: any }> {
    const response: { data: UrlInspectionResult | null; error: any } = { data: null, error: null };
    try {
      response.data = await firstValueFrom(
        this.core.post(`${environment.crawlerServiceUrl}/SearchConsole/inspect`, request)
      ) as UrlInspectionResult;
    } catch (error) {
      response.error = error;
    }
    return response;
  }

  private normalizeWorkspaceResponse(rawResponse: unknown): OpportunitiesWorkspaceResponse {
    if (Array.isArray(rawResponse)) {
      return {
        items: rawResponse as OpportunityItem[],
        sourceChecks: [],
      };
    }

    const candidate = rawResponse as Partial<OpportunitiesWorkspaceResponse> | null;
    const items = Array.isArray(candidate?.items) ? candidate.items : [];
    const sourceChecks = Array.isArray(candidate?.sourceChecks) ? candidate.sourceChecks : [];

    return {
      items,
      sourceChecks,
    };
  }
}
